// ToolConfig.tsx
// Centralized configuration for all chatbot tools.
// Each tool's unique properties are defined here.

import React from "react";

// Import all unique InfoSidebar components
import ChatGPTInfoSidebar from "./ChatGPTInfoSidebar";
import ClaudeInfoSidebar from "./ClaudeInfoSidebar";
import GeminiInfoSidebar from "./GeminiInfoSidebar";
import GrokInfoSidebar from "./GrokInfoSidebar";
import TradingViewInfoSidebar from "./TradingViewInfoSidebar";
import StableDiffusionInfoSidebar from "./StableDiffusionInfoSideBar";
import MathTutorInfoBar from "./MathTutorInfoBar";

// Import all unique Message components
import { ChatBotMessage, ChatBotMessageProps } from "./ChatBotMessage";
import { MathChatBotMessage, MathChatBotMessageProps } from "./MathChatBotMessage";
import { StockbotMessage, StockbotMessageProps } from "./StockbotMessage";

// ============================================================
// Bot-generated file attachment interface
// ============================================================
export interface BotFileAttachment {
  fileId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  description: string;
  downloadUrl: string;
}

// ============================================================
// Universal message props type (union of all message prop types)
// ============================================================
export type UniversalMessageProps =
  (ChatBotMessageProps | MathChatBotMessageProps | StockbotMessageProps) & {
    messageId?: string;
    responseId?: string;
    attachments?: {
      fileName: string;
      fileSize: number;
      fileType: string;
      uploadID?: number;
      previewUrl?: string;
      downloadUrl?: string;
    }[];
    botAttachments?: BotFileAttachment[];
    isLoading?: boolean;
    loadingText?: string;
    computerUseConfirm?: {
      schedule_tool_call_data: any;
      message: string;
    };
    sensitiveActionConfirm?: {
      schedule_tool_call_data: any;
      message: string;
    };
    // NEW: Structured props for UI rendering
    recipientDisambiguation?: {
      search_name: string;
      candidates: Array<{name: string, email: string}>;
      interim_text: string;
    };
    errorMessage?: string;
    deepAgentCard?: {
      title: string;
      message: string;
      tone: 'info' | 'success' | 'warning' | 'danger';
      runId?: number;
      approvalId?: number;
      artifacts?: any[];
      outputPayload?: any;
    };
  };

// ============================================================
// Tool configuration interface
// ============================================================
export interface ToolConfig {
  toolId: number;
  title: string;
  placeholder: string;
  fileAccept: string;
  dragDropAllowedExtensions: string[];
  InfoSidebar: React.ComponentType<{ isDark?: boolean }>;
  MessageComponent: React.ComponentType<any>;
}

// ============================================================
// Shared file type constants
// ============================================================

// Images
const IMAGE_ACCEPT = ".png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.bmp,.svg";
const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".heic", ".heif", ".bmp", ".svg"];

// Documents
const DOC_ACCEPT = ".pdf,.doc,.docx,.rtf,.odt,.ppt,.pptx";
const DOC_EXTENSIONS = [".pdf", ".doc", ".docx", ".rtf", ".odt", ".ppt", ".pptx"];

// Data & Spreadsheets
const DATA_ACCEPT = ".csv,.tsv,.xls,.xlsx,.json,.xml,.yaml,.yml,.toml";
const DATA_EXTENSIONS = [".csv", ".tsv", ".xls", ".xlsx", ".json", ".xml", ".yaml", ".yml", ".toml"];

// Plain Text & Markdown
const TEXT_ACCEPT = ".txt,.md,.mdx,.rst,.log";
const TEXT_EXTENSIONS = [".txt", ".md", ".mdx", ".rst", ".log"];

// Code
const CODE_ACCEPT = ".py,.js,.jsx,.ts,.tsx,.html,.htm,.css,.scss,.less,.java,.c,.cpp,.cs,.go,.rs,.php,.rb,.swift,.kt,.sql,.sh,.bash,.bat,.ps1,.env,.dockerfile";
const CODE_EXTENSIONS = [".py", ".js", ".jsx", ".ts", ".tsx", ".html", ".htm", ".css", ".scss", ".less", ".java", ".c", ".cpp", ".cs", ".go", ".rs", ".php", ".rb", ".swift", ".kt", ".sql", ".sh", ".bash", ".bat", ".ps1", ".env", ".dockerfile"];

// Combined for general-purpose LLM tools (documents + data + text + code + images)
const FULL_FILE_ACCEPT = `${DOC_ACCEPT},${DATA_ACCEPT},${TEXT_ACCEPT},${CODE_ACCEPT},${IMAGE_ACCEPT},image/*`;
const FULL_DRAG_DROP_EXTENSIONS = [...DOC_EXTENSIONS, ...DATA_EXTENSIONS, ...TEXT_EXTENSIONS, ...CODE_EXTENSIONS, ...IMAGE_EXTENSIONS];

// Image-only for Stable Diffusion
const IMAGE_ONLY_ACCEPT = `${IMAGE_ACCEPT},image/*`;
const IMAGE_ONLY_DRAG_DROP_EXTENSIONS = [...IMAGE_EXTENSIONS];

// ============================================================
// Configuration map: keyed by the selectedTool string from ToolSelector
// ============================================================
export const toolConfigurations: Record<string, ToolConfig> = {
  ChatGPT: {
    toolId: 1,
    title: "ChatGPT",
    placeholder: "Ask a question, schedule a repeating task, or create something.",
    fileAccept: FULL_FILE_ACCEPT,
    dragDropAllowedExtensions: FULL_DRAG_DROP_EXTENSIONS,
    InfoSidebar: ChatGPTInfoSidebar,
    MessageComponent: ChatBotMessage,
  },
  Claude: {
    toolId: 2,
    title: "Claude",
    placeholder: "Ask a question, schedule a repeating task, or create something.",
    fileAccept: FULL_FILE_ACCEPT,
    dragDropAllowedExtensions: FULL_DRAG_DROP_EXTENSIONS,
    InfoSidebar: ClaudeInfoSidebar,
    MessageComponent: ChatBotMessage,
  },
  Gemini: {
    toolId: 3,
    title: "Gemini",
    placeholder: "Ask a question, schedule a repeating task, or create something.",
    fileAccept: FULL_FILE_ACCEPT,
    dragDropAllowedExtensions: FULL_DRAG_DROP_EXTENSIONS,
    InfoSidebar: GeminiInfoSidebar,
    MessageComponent: ChatBotMessage,
  },
  Grok: {
    toolId: 4,
    title: "Grok",
    placeholder: "Ask a question, schedule a repeating task, or create something.",
    fileAccept: FULL_FILE_ACCEPT,
    dragDropAllowedExtensions: FULL_DRAG_DROP_EXTENSIONS,
    InfoSidebar: GrokInfoSidebar,
    MessageComponent: ChatBotMessage,
  },
  TradingView: {
    toolId: 5,
    title: "TradingView",
    placeholder: "Ask a question, schedule a repeating task, or create something.",
    fileAccept: FULL_FILE_ACCEPT,
    dragDropAllowedExtensions: FULL_DRAG_DROP_EXTENSIONS,
    InfoSidebar: TradingViewInfoSidebar,
    MessageComponent: StockbotMessage,
  },
  "Stable Diffusion": {
    toolId: 6,
    title: "Stable Diffusion",
    placeholder: "Ask Stable Diffusion for an image...",
    fileAccept: IMAGE_ONLY_ACCEPT,
    dragDropAllowedExtensions: IMAGE_ONLY_DRAG_DROP_EXTENSIONS,
    InfoSidebar: StableDiffusionInfoSidebar,
    MessageComponent: ChatBotMessage,
  },
  Math: {
    toolId: 7,
    title: "Math Tutor",
    placeholder: "Ask a math question...",
    fileAccept: FULL_FILE_ACCEPT,
    dragDropAllowedExtensions: FULL_DRAG_DROP_EXTENSIONS,
    InfoSidebar: MathTutorInfoBar,
    MessageComponent: MathChatBotMessage,
  },
};